import { MODEL_INPUT_LIMITS } from '@/components/Nodes/ModelComponents/consts/model-input-limits.const';
import type { Handle } from '@/types/node';

export interface ModelInputLimitStatus {
  /** True when the model's dynamic-input cap has been reached. */
  reachedLimit: boolean;
  /** The cap, or null when the model has no configured limit. */
  maxInputs: number | null;
}

/**
 * Counts a model node's existing dynamic input handles against its {@link MODEL_INPUT_LIMITS} cap.
 * Models with no entry — or whose handles are declared as a static array — are treated as uncapped.
 */
export function getModelInputLimitStatus(
  modelName: string,
  inputHandles: string[] | Record<string, Handle>,
  inputPrefix?: string,
): ModelInputLimitStatus {
  const modelLimits = MODEL_INPUT_LIMITS[modelName];
  if (!modelLimits || Array.isArray(inputHandles)) {
    return { reachedLimit: false, maxInputs: null };
  }
  const prefixLimit = inputPrefix ? modelLimits.maxInputsByPrefix?.[inputPrefix] : undefined;
  if (
    prefixLimit !== undefined &&
    Object.keys(inputHandles).filter((key) => key.startsWith(`${inputPrefix}_`)).length >= prefixLimit
  ) {
    return { reachedLimit: true, maxInputs: prefixLimit };
  }
  // The dynamic keys `addInputHandleWithKey` mints are `prefix` (the bare first handle) and
  // `prefix_N`. Matching on `includes` instead also caught unrelated handles that merely contain
  // the prefix — Meshy 7.1's `texture_image_url` against its `image_N` inputs — which spent one of
  // the model's slots and blocked the last real input.
  const matchingHandles = Object.keys(inputHandles).filter((key) =>
    modelLimits.inputPrefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}_`)),
  ).length;
  const currentCount = Math.floor(matchingHandles / (modelLimits.handlesPerInput ?? 1));
  return { reachedLimit: currentCount >= modelLimits.maxInputs, maxInputs: modelLimits.maxInputs };
}
